import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Landing from "@/pages/Landing";
import AdminLogin from "@/pages/AdminLogin";
import Dashboard from "@/pages/Dashboard";
import AdminCompetitionView from "@/pages/AdminCompetitionView";
import AdminCourses from "@/pages/AdminCourses";
import AdminLeaderboard from "@/pages/AdminLeaderboard";
import AdminUtilities from "@/pages/AdminUtilities";
import AdminSendEmail from "@/pages/AdminSendEmail";
import PlayerManagement from "@/pages/PlayerManagement";
import PlayerEnterScore from "@/pages/PlayerEnterScore";
import PlayerViewBest from "@/pages/PlayerViewBest";
import Register from "@/pages/Register";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import LegalPage from "@/pages/LegalPage";
import AdminLegalEditor from "@/pages/AdminLegalEditor";
import Round1MockupPreview from "@/pages/Round1MockupPreview";
import ScorecardMockupPreview from "@/pages/ScorecardMockupPreview";
import HeroMockup1 from "@/pages/HeroMockup1";
import HeroMockup2 from "@/pages/HeroMockup2";
import HeroMockup3 from "@/pages/HeroMockup3";
import Settings from "@/pages/Settings";
import NetworkStatus from "@/components/NetworkStatus";
import HelpBot from "@/components/HelpBot";
import TermsAcceptanceDialog from "@/components/TermsAcceptanceDialog";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Build identifier. Surfaced in the footer + the session-error card so we can
// instantly tell whether a deployment is on the latest code (vs. a stale
// bundle that's caused multiple rounds of misdiagnosed auth bugs).
// Update the date suffix any time a significant deploy goes out.
export const BUILD_TAG = process.env.REACT_APP_BUILD_ID || "2026-05-21-withdraw-fix";

axios.defaults.withCredentials = true;

// CRITICAL: Self-healing same-origin API call rewriter.
//
// Why this exists: when REACT_APP_BACKEND_URL is set to a different host than the
// page is served from (e.g., production bundle baked with `golf-score-admin.emergent.host`
// but page served from `golfeclectic.net`), cookies set by the backend are stored against
// the BACKEND_URL host and treated as third-party by the browser. iOS Safari and most
// mobile browsers BLOCK third-party cookies by default — so login appears to succeed
// (Set-Cookie header arrives) but the cookie is never sent back on subsequent requests
// and every /auth/me returns 401.
//
// Fix: at request time, if the target URL's host differs from the current window's host,
// rewrite to the same origin so cookies are first-party. This is safe because Cloudflare
// + Emergent ingress route `/api/*` on the production domain to the same backend.
//
// Exceptions: a couple of intentionally external endpoints (Emergent OAuth proxy) but
// those are navigated to via window.location, not axios, so they're not affected.
if (typeof window !== "undefined") {
  axios.interceptors.request.use((config) => {
    try {
      if (!config.url) return config;
      const target = new URL(config.url, window.location.origin);
      if (target.host !== window.location.host && (target.pathname.startsWith("/api/") || target.pathname === "/api")) {
        const rewritten = window.location.origin + target.pathname + target.search;
        config.url = rewritten;
      }
    } catch (_e) {
      /* malformed URL — leave it alone */
    }
    return config;
  });
}

// Add axios interceptor for offline detection
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!navigator.onLine) {
      error.message = 'You are offline. Please check your internet connection.';
      error.isOffline = true;
    }
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = React.createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: Per Emergent Auth playbook, when returning from OAuth callback the URL
    // contains #session_id=... but the session cookie has NOT been established yet.
    // If we call /auth/me here it returns 401 and sets user=null, which then races with
    // SessionHandler's subsequent checkAuth — ProtectedRoute reads the stale null and
    // bounces the user back to "/". Skip this initial fetch and let SessionHandler
    // populate the user once the session has been exchanged.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      setUser(null);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// Protected Route
function ProtectedRoute({ children }) {
  const { user, loading } = React.useContext(AuthContext);
  const location = useLocation();

  // If we just came back from the OAuth callback, the route was navigated to with the
  // freshly-exchanged user in location.state. Trust that and render immediately —
  // AuthProvider's state will catch up by the next render.
  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <div className="text-lg text-accent-deep">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Session Handler Component
// Renders ONLY during an OAuth callback (when window.location.hash contains
// `session_id=...`). When mounted, it shows a clean spinner instead of letting
// the Landing page flash up briefly behind it.
//
// On failure we surface the error visibly with a retry button instead of
// silently redirecting to `/` — the silent redirect made the iOS Safari
// 3rd-party-cookie bug invisible to users and to debugging.
function SessionHandler() {
  const processedRef = React.useRef(false);
  const [status, setStatus] = React.useState("loading"); // loading | error
  const [errorMsg, setErrorMsg] = React.useState("");
  const [errorDetail, setErrorDetail] = React.useState("");

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=') && !processedRef.current) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      if (sessionId) {
        processedRef.current = true;
        handleSession(sessionId);
      }
    }
  }, []);

  const handleSession = async (sessionId) => {
    try {
      // 1. Exchange session_id for an httpOnly cookie. Backend returns the full
      //    user record in the response body so we don't need a follow-up
      //    /auth/me call (that second call was racing with cookie propagation).
      const sessionResp = await axios.post(`${API}/auth/session`, { session_id: sessionId }, {
        withCredentials: true,
        timeout: 12000,
      });

      // 2. Validate the payload BEFORE touching window.history. If the body is
      //    incomplete, show the visible error card with the build tag.
      const userData = sessionResp?.data?.user || null;
      if (!userData) {
        setErrorMsg("Session response was incomplete.");
        setErrorDetail("The server accepted your sign-in but didn't return your account. Please try again.");
        setStatus("error");
        return;
      }

      // 3. SUCCESS PATH — use a full-page redirect to /dashboard instead of
      //    React Router's `navigate`. This is the standard OAuth-completion
      //    pattern (Auth0/Google/Stripe all do this) and matters here because:
      //
      //    a) `window.history.replaceState` to clear the hash, followed by an
      //       SPA `navigate()`, was racing with AppRouter's hash-driven re-mount
      //       decision — on iOS Safari the spinner would persist indefinitely
      //       even though the backend session was already created (cookie set,
      //       new-tab loads showed the user fully logged in).
      //    b) A full reload guarantees AuthProvider's `checkAuth()` runs against
      //       the freshly-set cookie, so the Dashboard renders deterministically.
      //    c) The cookie is httpOnly + same-origin (set by the interceptor on
      //       golfeclectic.net), so no propagation race remains.
      //
      // Clear the hash first so the reload doesn't re-trigger SessionHandler.
      window.history.replaceState(null, '', window.location.pathname);
      window.location.replace('/dashboard');
    } catch (error) {
      console.error('Session processing error:', error);
      // Build a human-readable error message we can show on screen. The silent
      // redirect-to-home pattern previously hid this from users (and from us).
      let msg = "We couldn't complete sign-in.";
      let detail = "";
      if (error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "")) {
        msg = "Sign-in timed out.";
        detail = "The server didn't respond in time. This usually means a network issue or a stale deployment. Please try again.";
      } else if (error?.response?.status === 401) {
        msg = "Sign-in session expired.";
        detail = "Your one-time sign-in token was already used or has expired. Tap retry to start over.";
      } else if (error?.response?.data?.detail) {
        detail = typeof error.response.data.detail === "string"
          ? error.response.data.detail
          : JSON.stringify(error.response.data.detail);
      } else if (error?.message) {
        detail = error.message;
      }
      setErrorMsg(msg);
      setErrorDetail(detail);
      setStatus("error");
    }
  };

  const handleRetry = () => {
    // Send the user back to the landing page so they can re-initiate the OAuth
    // flow from scratch. We clear the hash so a stale session_id can't loop.
    window.location.replace('/');
  };

  if (status === "error") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-app px-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-line shadow-sm p-6 text-center" data-testid="session-error-card">
          <div className="mx-auto w-12 h-12 rounded-full bg-warm-tint flex items-center justify-center mb-4">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-warm">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold tracking-tight text-ink" data-testid="session-error-title">{errorMsg}</h2>
          {errorDetail && <p className="text-sm text-ink-muted mt-2 leading-relaxed" data-testid="session-error-detail">{errorDetail}</p>}
          <div className="mt-5 flex flex-col gap-2">
            <button
              onClick={handleRetry}
              data-testid="session-error-retry"
              className="ge-btn-primary justify-center !py-3"
            >
              Try signing in again
            </button>
          </div>
          <p className="text-[11px] text-ink-soft mt-5 font-mono">
            build {BUILD_TAG}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-app">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
        <div className="text-lg text-accent-deep font-medium">Signing you in...</div>
        <div className="text-sm text-ink-soft">Please wait a moment</div>
        <div className="text-[10px] text-ink-soft/60 font-mono pt-4">build {BUILD_TAG}</div>
      </div>
    </div>
  );
}

// AppRouter decides BEFORE Routes mounts whether we're in the middle of an
// OAuth callback. If yes, render ONLY the SessionHandler (no Landing flicker).
// Otherwise, render nothing here and let the normal <Routes> below take over.
function AppRouter({ children }) {
  const isAuthCallback = typeof window !== 'undefined'
    && window.location.hash?.includes('session_id=');

  if (isAuthCallback) {
    return <SessionHandler />;
  }

  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NetworkStatus />
        <AppRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/legal/:pageType" element={<LegalPage />} />
          <Route path="/preview/round1" element={<Round1MockupPreview />} />
          <Route path="/preview/scorecard" element={<ScorecardMockupPreview />} />
          <Route path="/preview/hero1" element={<HeroMockup1 />} />
          <Route path="/preview/hero2" element={<HeroMockup2 />} />
          <Route path="/preview/hero3" element={<HeroMockup3 />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/competitions"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="/admin/competitions/:id"
            element={
              <ProtectedRoute>
                <AdminCompetitionView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/competitions/:id/leaderboard"
            element={
              <ProtectedRoute>
                <AdminLeaderboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/players"
            element={
              <ProtectedRoute>
                <PlayerManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/courses"
            element={
              <ProtectedRoute>
                <AdminCourses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/utilities"
            element={
              <ProtectedRoute>
                <AdminUtilities />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/send-email"
            element={
              <ProtectedRoute>
                <AdminSendEmail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/legal"
            element={
              <ProtectedRoute>
                <AdminLegalEditor />
              </ProtectedRoute>
            }
          />
          {/* /player/competitions consolidated into /dashboard (Option B). Legacy URL → redirect. */}
          <Route path="/player/competitions" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/player/competitions/:id/enter-score"
            element={
              <ProtectedRoute>
                <PlayerEnterScore />
              </ProtectedRoute>
            }
          />
          <Route
            path="/player/competitions/:id/summary"
            element={
              <ProtectedRoute>
                <PlayerViewBest />
              </ProtectedRoute>
            }
          />
        </Routes>
        </AppRouter>
        <Toaster position="top-center" />
        <HelpBot />
        <TermsAcceptanceDialog />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;